import * as React from 'react'
import { useEscapeListener } from 'app/hooks/useEscapeListener'

export interface ConfirmationOptions {
  variant: `danger` | `info`
  title: string
  description: string
  submitLabel?: string
  cancelLabel?: string
}

export type UseConfirmationAlertReturn = {
  open: boolean
  options: ConfirmationOptions
  onSubmit: () => void
  onClose: () => void
  onExited: () => void
}

export type OpenConfirmation = (options: ConfirmationOptions) => Promise<void>

export function useConfirmationAlert(): {
  providerValue: [OpenConfirmation, boolean]
  alertProps: UseConfirmationAlertReturn
} {
  const [confirmationState, setConfirmationState] = React.useState<ConfirmationOptions | null>(null)
  const [open, setOpen] = React.useState<boolean>(false)
  useEscapeListener(() => setOpen(false), open)

  const awaitingPromiseRef = React.useRef<{
    resolve: () => void
    reject: () => void
  }>(undefined)

  const openConfirmation: OpenConfirmation = (options) => {
    setConfirmationState(options)
    setOpen(true)
    return new Promise<void>((resolve, reject) => {
      awaitingPromiseRef.current = { resolve, reject }
    })
  }

  const onClose = () => setOpen(false)

  const onSubmit = () => {
    awaitingPromiseRef.current?.resolve()
    setOpen(false)
  }

  const onExited = () => setConfirmationState(null)

  return {
    providerValue: [openConfirmation, open],
    alertProps: {
      open,
      options: {
        variant: `danger`,
        title: `Confirm action`,
        description: `Are you sure you want to proceed?`,
        ...confirmationState,
      },
      onSubmit,
      onClose,
      onExited,
    },
  }
}
